<script setup lang="ts">
    import { computed, onMounted, ref, watch, watchEffect } from 'vue'
    import { useRoute } from 'vue-router'
    import {
        useAlertStore,
        useAuthStore,
        useFachbereichStore,
        useGeneralStore,
        useKennzahlStudiengangStore,
        useLayoutStore,
        useModuleStore,
        useModulHandbuchStore,
        useStudiengangStore,
        useUserStore
    } from '@/js/stores'
    import store from '@/js/store'
    import logo from '../../images/HSA_Logo_digital_RGB_1000px_ohne_Schriftzug.png'
    import router from '@/js/router/index.js'
    import { useDebounceFn, useElementSize, useStorage } from '@vueuse/core'
    import { FetchParams } from '@/js/utils/fetch-params'

    const appBarRef = ref<HTMLElement | null>(null)
    const { height } = useElementSize(appBarRef)
    const isAdminAllowedToClearEventStore = ref(
        process.env.IS_ADMIN_USER_ALLOWED_TO_CLEAR_EVENT_STORE
    )
    const adminRoutes = ref([
        'Users',
        'Studiengaenge',
        'ModulHandBuchControl',
        'ModulControl',
        'ErrorLogs',
        'TooltipControl'
    ])
    const lernleitstandLogo = ref(logo)
    const clearEventStoreDialog = ref(false)

    const alertStore = useAlertStore()
    const authStore = useAuthStore()
    const fachbereichStore = useFachbereichStore()
    const generalStore = useGeneralStore()
    const kennzahlStudiengangStore = useKennzahlStudiengangStore()
    const modulStore = useModuleStore()
    const modulhandbuchStore = useModulHandbuchStore()
    const studiengangStore = useStudiengangStore()
    const userStore = useUserStore()

    const route = useRoute()
    const layoutStore = useLayoutStore()

    const drawer = ref(false)
    const users = ref([])
    const userFilter = ref([])

    watchEffect(() => {
        layoutStore.appBarHeight = height.value
    })

    const verwaltungToggleClass = computed(() => {
        return adminRoutes.value.indexOf(route.name) > -1
    })

    const isAuthenticated = computed(() => store.getters['security/isAuthenticated'])
    const isAdmin = computed(() => store.getters['security/isAdmin'])
    const canViewKennwerte = computed(() => store.getters['security/canViewKennwerte'])
    const currentUser = computed(() => store.getters['security/currentUser'])
    const isUserSwitched = computed(() => store.getters['security/isUserSwitched'])
    const displayedUserList = computed(() => {
        if (!users.value || !users.value.length) {
            return []
        }

        return users.value
            .slice(0)
            .filter((user) => {
                if (
                    Object.keys(user.rollen).some((rollenName) =>
                        rollenName.toLowerCase().includes(userFilter.value.toLowerCase())
                    )
                ) {
                    return true
                }

                return user.email.email.toLowerCase().includes(userFilter.value.toLowerCase())
            })
            .sort((a, b) => {
                return a.email.email > b.email.email ? 1 : -1
            })
    })

    const loadUsers = async () => {
        const params: FetchParams = {
            page: 1,
            itemsPerPage: 100,
            sortBy: 'nachname',
            ['identifier']: userFilter.value
        }

        const data = await userStore.fetchPaginatedUsers(params)

        users.value = data['hydra:member']
    }

    const isLoading = computed(() => {
        return (
            store.getters['logfile/isLoading'] ||
            store.getters['modul/isLoading'] ||
            store.getters['modulHandBuch/isLoading'] ||
            store.getters['security/isLoading'] ||
            store.getters['user/isLoading'] ||
            authStore.isLoading ||
            fachbereichStore.isLoading ||
            generalStore.isLoading ||
            kennzahlStudiengangStore.isLoading ||
            modulStore.isLoading ||
            modulhandbuchStore.isLoading ||
            studiengangStore.isLoading ||
            userStore.isLoading
        )
    })

    const userFilterState = useStorage('nav-user-filter', '')

    watch(
        userFilter,
        useDebounceFn(() => {
            userFilterState.value = userFilter.value
            loadUsers()
        }, 500)
    )

    onMounted(async () => {
        userFilter.value = userFilterState.value
        users.value = loadUsers()
    })

    const switchUser = (user): void => {
        store.dispatch('security/switchUser', user)
    }
    const exitSwitchUser = (): void => {
        store.dispatch('security/exitSwitchUser')
    }
    const showUserMenu = async (): void => {
        if (isAdmin.value && users.value.length <= 1) {
            loadUsers()
        }
    }
    const handleClearEventStore = async (): void => {
        let response = await store.dispatch('clearStore')
        if (201 === response.status) {
            alertStore.success('Du wurdest abgemeldet.', 'Daten komplett zurückgesetzt')
            router.push({ name: 'Logout' })
        } else {
            alertStore.alert(response.data['hydra:description'], 'Ein Fehler ist aufgetreten')
        }
    }
</script>

<template>
    <v-app-bar color="primary" dark ref="appBarRef">
        <v-container fluid class="d-flex align-center justify-space-between">
            <div class="d-flex align-center w-100 w-md-auto">
                <v-app-bar-nav-icon class="d-md-none mr-5" @click="drawer = !drawer" />
                <v-spacer class="d-md-none" />
                <v-app-bar-title>
                    <router-link class="btn btn-link" :to="{ name: 'ModulhandbuchPage' }">
                        <img
                            :src="lernleitstandLogo"
                            class="lenin-navbar-logo py-1"
                            alt="lernleitstand-logo"
                        />
                    </router-link>
                </v-app-bar-title>
            </div>
            <div class="d-none d-md-flex justify-center flex-grow-1">
                <v-spacer class="d-none d-md-block" />
                <v-btn
                    v-if="canViewKennwerte"
                    :to="{ name: 'DashboardPage' }"
                    class="nav-item nav-link btn"
                    data-test="dashboard-page-nav"
                >
                    Dashboard
                </v-btn>
                <v-btn
                    v-if="isAuthenticated"
                    :to="{ name: 'ModulhandbuchPage' }"
                    class="nav-item nav-link btn"
                    data-test="modulhandbuch-page-nav"
                >
                    Modulhandbücher
                </v-btn>

                <div class="d-flex align-center">
                    <v-menu v-if="isAdmin">
                        <template #activator="{ props: activatorProps }">
                            <v-btn
                                v-bind="activatorProps"
                                data-test="verwaltung-nav"
                                :active="verwaltungToggleClass"
                            >
                                Verwaltung
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item link :to="{ name: 'KennzahlenVerwaltungPage' }">
                                <v-list-item-title data-test="kennzahlen-nav">
                                    Kennzahlen
                                </v-list-item-title>
                            </v-list-item>
                            <v-divider />
                            <v-list-item link :to="{ name: 'Studiengaenge' }">
                                <v-list-item-title data-test="studiengaenge-nav">
                                    Studiengänge
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item link :to="{ name: 'Users' }">
                                <v-list-item-title data-test="users-nav">
                                    Benutzer
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item link :to="{ name: 'ModulHandBuchControl' }">
                                <v-list-item-title data-test="modulhandbuch-control-nav">
                                    Modulhandbücher
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item link :to="{ name: 'ModulControl' }">
                                <v-list-item-title data-test="modul-control-nav">
                                    Modulübersicht
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item link :to="{ name: 'TooltipControl' }">
                                <v-list-item-title data-test="tooltip-control-nav">
                                    Tooltips
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item link :to="{ name: 'ErrorLogs' }">
                                <v-list-item-title data-test="error-logs-nav">
                                    <v-icon
                                        icon="mdi-math-log"
                                        class="text-info-emphasis"
                                        size="20"
                                    />
                                    ErrorLogs anschauen
                                </v-list-item-title>
                            </v-list-item>
                            <v-divider v-if="isAdminAllowedToClearEventStore" />
                            <v-list-item
                                v-if="isAdminAllowedToClearEventStore"
                                link
                                @click="clearEventStoreDialog = true"
                            >
                                <v-list-item-title data-test="top-menu-reset-data">
                                    <v-icon
                                        icon="mdi-alert-box"
                                        class="text-danger-emphasis"
                                        size="20"
                                    />
                                    Daten komplett zurücksetzen
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
                <v-spacer class="d-none d-md-block" />

                <v-menu :close-on-content-click="false" width="400" @input="showUserMenu">
                    <template #activator="{ props }">
                        <v-btn
                            data-test="nav-user-item"
                            variant="text"
                            v-bind="props"
                            class="text-transform-none"
                            append-icon="mdi-menu-down"
                        >
                            <v-icon>mdi-account</v-icon>
                            <span v-if="isAuthenticated" class="d-none d-sm-block ml-1">{{
                                currentUser?.email?.email
                            }}</span>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item v-if="!isAuthenticated" :to="{ name: 'Login' }" exact link>
                            <v-list-item-title>Login</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            v-if="!isAuthenticated"
                            :to="{ name: 'PasswordReset' }"
                            exact
                            link
                        >
                            <v-list-item-title>Passwort vergessen?</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            v-if="isAuthenticated"
                            :to="{ name: 'PasswordChangeRequest' }"
                            exact
                            link
                        >
                            <v-list-item-title>Passwort ändern</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            v-if="isUserSwitched"
                            data-test="exit-switch-user"
                            @click="exitSwitchUser"
                        >
                            <v-list-item-title>Nutzerwechsel beenden</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            v-if="isAuthenticated"
                            :to="{ name: 'Logout' }"
                            exact
                            link
                            data-test="nav-user-logout"
                        >
                            <v-list-item-title>Abmelden</v-list-item-title>
                        </v-list-item>
                        <v-divider v-if="!isUserSwitched && isAdmin" />
                        <v-list-item v-if="!isUserSwitched && isAdmin">
                            <v-list-item-title>Nutzerwechsel</v-list-item-title>
                            <v-text-field
                                v-model="userFilter"
                                label="Benutzername"
                                type="search"
                                clearable
                                variant="outlined"
                                density="compact"
                                hide-details
                                class="mt-1"
                                placeholder="Benutzername eingeben"
                            />
                        </v-list-item>
                        <v-divider v-if="!isUserSwitched && isAdmin" />
                        <v-list
                            v-if="!isUserSwitched && isAdmin"
                            density="compact"
                            nav
                            color="white"
                            class="white"
                        >
                            <v-list-item
                                v-for="(user, key) in displayedUserList"
                                :key="key"
                                density="compact"
                                :disabled="!user.isEnabled"
                                @click="switchUser(user)"
                            >
                                <v-list-item-title>{{ user.email.email }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{
                                        Object.values(user.rollen)
                                            .map((currentRolle) => currentRolle.rollenName.name)
                                            .join(', ')
                                    }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </v-list>
                </v-menu>
            </div>
        </v-container>
        <v-progress-linear
            :active="isLoading"
            :indeterminate="isLoading"
            color="info"
            absolute
            bottom
        />
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" app temporary color="#454545" dark>
        <v-list>
            <v-list-item v-if="canViewKennwerte" link :to="{ name: 'DashboardPage' }">
                <v-list-item-title>Dashboard</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAuthenticated" link :to="{ name: 'ModulhandbuchPage' }">
                <v-list-item-title>Modulhandbücher</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAdmin" link :to="{ name: 'KennzahlenVerwaltungPage' }">
                <v-list-item-title>Kennzahlen-Verwaltung</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAdmin" link :to="{ name: 'Studiengaenge' }">
                <v-list-item-title>Studiengänge</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAdmin" link :to="{ name: 'Users' }">
                <v-list-item-title>Benutzer</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAdmin" link :to="{ name: 'ModulHandBuchControl' }">
                <v-list-item-title>Modulhandbücher</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAdmin" link :to="{ name: 'ModulControl' }">
                <v-list-item-title>Modulübersicht</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAdmin" link :to="{ name: 'TooltipControl' }">
                <v-list-item-title>Tooltips</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAdmin" link :to="{ name: 'ErrorLogs' }">
                <v-list-item-title>
                    <v-icon icon="mdi-math-log" class="text-info-emphasis" size="20" />
                    ErrorLogs anschauen
                </v-list-item-title>
            </v-list-item>

            <v-divider v-if="isAdminAllowedToClearEventStore && isAdmin" />
            <v-list-item
                v-if="isAdminAllowedToClearEventStore && isAdmin"
                link
                @click="clearEventStoreDialog = true"
            >
                <v-list-item-title data-test="side-menu-reset-data">
                    <v-icon icon="mdi-alert-box" class="text-danger-emphasis" size="20" />
                    Daten komplett zurücksetzen
                </v-list-item-title>
            </v-list-item>
            <template v-if="!isAuthenticated">
                <v-list-item v-if="!isAuthenticated" :to="{ name: 'Login' }" exact link>
                    <v-list-item-title>Login</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="!isAuthenticated" :to="{ name: 'PasswordReset' }" exact link>
                    <v-list-item-title>Passwort vergessen?</v-list-item-title>
                </v-list-item>
            </template>
        </v-list>
    </v-navigation-drawer>
    <v-dialog v-model="clearEventStoreDialog">
        <v-card>
            <v-card-title>Daten komplett zurücksetzen?</v-card-title>
            <v-card-text>
                <v-alert
                    class="w-100 text-muted mb-0"
                    variant="tonal"
                    color="warning"
                    prominent
                    icon="mdi-alert-box"
                >
                    <b>Warnung:</b>
                    <ul class="mb-0">
                        <li>Alle bereits importierten Daten werden zurückgesetzt.</li>
                        <li>Bereits erfolgte Änderungen gehen verloren.</li>
                        <li>Es werden die Fachbereiche neu erstellt.</li>
                        <li>Es wird der Admin-Nutzer neu erstellt.</li>
                    </ul>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-btn @click="clearEventStoreDialog = false"> Abbruch </v-btn>
                <v-btn color="warning" @click="handleClearEventStore">
                    Ja, Daten komplett zurücksetzen
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped></style>

<style lang="scss">
    $nav-item-height: 45px;

    .lenin-navbar-logo {
        max-height: $nav-item-height !important;
        flex: 1 1 auto;
    }
</style>
